import React, { useEffect, useMemo, useRef } from "react";
import { Alert, AlertTitle, Button, Card, IconButton, Stack, Typography, useTheme } from "@mui/material";
import * as default_data from '../../config.js';
import * as log from 'loglevel'
import { getToken } from "../../AuthService.js";
import { MarkdownRenderer } from "../MarkdownRenderer.js";
import { sanitizeHtml } from '../../Utils/text_functions';
import { Reference } from "../Components/Reference.js";
import { MdOutlineOpenInNew } from "react-icons/md";
import { get_loading_message } from '../../Utils/file_functions.js'

export const DeepAgent = ({ deep_agent_id, deepAgentSidebarRef=null, addOrUpdatFinalMessage, setDeepAgentFinalResponse }) => {
  const theme = useTheme();

  const [deep_agent_infos, setRDeepAgentInfos] = React.useState({'infos': [], 'actions': [], 'references': []});

  const [deep_agent_response, setDeepAgentResponse] = React.useState(get_loading_message(theme));
  const [deep_agent_status, setDeepAgentStatus] = React.useState('Deep Agent in progress');
  const [deep_agent_error_message, setDeepAgentErrorMessage] = React.useState('');
  const [deep_agent_finished, setDeepAgentFinished] = React.useState(false);
  const [showExplainability, setShowExplainability] = React.useState(false);

  // Add this near your other state and ref declarations
  const deepAgentInfosRef = useRef(deep_agent_infos);

  // Then modify your onClick function to use the ref
  const onClick = () => {
    if(deepAgentSidebarRef !== null && deepAgentSidebarRef.current !== null && deepAgentInfosRef !== null && deepAgentInfosRef.current !== null) {
      // Pass the deep_agent_id when setting infos
      deepAgentSidebarRef.current.setInfos(deepAgentInfosRef.current, parseInt(deep_agent_id));
      deepAgentSidebarRef.current.openDrawer();
    }
  };

  const setDeepAgentInfos = (deep_agent_obj) => {
    setRDeepAgentInfos(deep_agent_obj);
    deepAgentInfosRef.current = deep_agent_obj;
    
    if(deepAgentSidebarRef !== null && deepAgentSidebarRef.current !== null) {
      // Check if the sidebar is open and showing this agent's info
      const currentAgentId = deepAgentSidebarRef.current.getCurrentAgentId();
      if (currentAgentId === null || currentAgentId === parseInt(deep_agent_id)) {
        // Only update if the sidebar is showing this agent's info or no agent info
        deepAgentSidebarRef.current.setInfos(deep_agent_obj, parseInt(deep_agent_id));
      }
    }
  };

  const fetchDeepAgentInfo = async () => {
    // clean up error state if we are fetching new data
    setDeepAgentErrorMessage('');

    try {
      let deep_agent_obj = {'infos': [], 'actions': [], 'references': []};
      setDeepAgentInfos(deep_agent_obj);

      var token = getToken();
      if (token === false) {
        return;
      }

      const response = await fetch(default_data.default_chat_service_url + '/get-deep-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },
        body: JSON.stringify({
          deep_agent_id: deep_agent_id
        })
      });
    
      // Check if the response is ok
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8'); // Create a TextDecoder instance

      let bufferLeft = ''

      while(true) {
        const {value, done} = await reader.read();
        try {
          if(value === undefined) {
            // check if bufferLeft not empty and if starts and ends with {
            if (bufferLeft !== '' && bufferLeft.startsWith('{')) {
              try {
                let http_response = JSON.parse(bufferLeft);
                if(http_response.status !== 200 && http_response.status !== '200') {
                  log.error(`/fetchDeepAgentInfo: Error 601: ${bufferLeft} - deep_agent_id: ${deep_agent_id}`);
                  setDeepAgentErrorMessage('An error occurred while fetching the deep agent info. Please contact customer support.');
                }
              }
              catch (error) {
                log.error(`/fetchDeepAgentInfo: ${error.message}`);
                log.error(`/fetchDeepAgentInfo: ${error.stack}`);
              }
            }
            return;
          }
          if (done) {
            if(bufferLeft !== '') {
              let ret2 = handleData(deep_agent_obj, bufferLeft);
              if(ret2 === null) {
                setDeepAgentErrorMessage('An error occurred while fetching the deep agent info. Please contact customer support.');
              }
              bufferLeft = ''
            }
            break;
          } 
          let tmp_text = decoder.decode(value); // Decode the Uint8Array into a string

          let cur_items_text = bufferLeft + tmp_text;

          if(cur_items_text.indexOf('@|@sep@|@') !== -1) {
            let cur_items = cur_items_text.split('@|@sep@|@'); // Split the chunk into lines
            for (const cur_item of cur_items) {
              if(cur_item !== '') {
                let err = handleData(deep_agent_obj, cur_item);
                if(err === null) {
                  bufferLeft += cur_item;
                }
                else {
                  bufferLeft = '';
                }
              }
            }  
          }
          else {
              bufferLeft = cur_items_text;
          }        
        } catch (error) {
          // For other errors, log and potentially update UI
          log.error(`/fetchDeepAgentInfo: Error 1: ${error.message}`);
          log.error(`/fetchDeepAgentInfo: Error stack 2: ${error.stack}`);
          throw error; // Rethrow to be handled by caller
        } 
      }    
    } catch (error) {
      // Handle fetch errors
      log.error(`/fetchDeepAgentInfo: Error 2: ${error.message}`);
      log.error(`/fetchDeepAgentInfo: Error stack 3: ${error.stack}`);
    }
  };
  
  function upsertById(array, newObj) {
    // Find index of object with the same id
    const idx = array.findIndex(item => item.id === newObj.id);
    if (idx !== -1) {
      // Update existing object
      array[idx] = { ...array[idx], ...newObj };
    } else {
      // Add new object
      array.push(newObj);
    }
  }

  function handleData(deep_agent_obj, data, log_error=false) {
    try {
      const parsedData = JSON.parse(data);
      if (parsedData.type === 'deep_agent_info' || parsedData.type === 'deep_agent_action' || parsedData.type === 'deep_agent_reference' || parsedData.type === 'deep_agent') {
        try {
          const parsedMessage = JSON.parse(parsedData.message);
          if (parsedMessage !== null) {
            if (parsedMessage['deep_agent_id'] !== undefined && parsedMessage['deep_agent_id'] !== null) {
              // check if deep_agent_id is a string and convert to int
              if (typeof parsedMessage['deep_agent_id'] === 'string') {
                parsedMessage['deep_agent_id'] = parseInt(parsedMessage['deep_agent_id']);
              } 
              // check if parseInt succeeded
              if (isNaN(parsedMessage['deep_agent_id'])) {
                log.error('Error parsing deep_agent_id:', parsedMessage['deep_agent_id']);
                return null;
              }
            }
            else {
              // deep_agent_id is called id
              if (parsedMessage['id'] !== undefined && parsedMessage['id'] !== null) {
                if (typeof parsedMessage['id'] === 'string') {
                  parsedMessage['deep_agent_id'] = parseInt(parsedMessage['id']);
                }
                else {
                  parsedMessage['deep_agent_id'] = parsedMessage['id'];
                }
                // check if parseInt succeeded
                if (isNaN(parsedMessage['deep_agent_id'])) {
                  log.error('Error parsing deep_agent_id:', parsedMessage['id']);
                  return null;
                }
              }              
              else {
                log.error('Error: deep_agent_id not found in message:', parsedMessage);
                return null;
              }
            }

            if(parsedMessage['deep_agent_id'] !== parseInt(deep_agent_id)) {
              log.error('Error: deep_agent_id does not match:', parsedMessage['deep_agent_id'], deep_agent_id);
              return null;
            }
            if(parsedData.type === 'deep_agent_info') {
              upsertById(deep_agent_obj.infos, parsedMessage);
            }
            else if(parsedData.type === 'deep_agent_action') {
              upsertById(deep_agent_obj.actions, parsedMessage);
              if(parsedMessage['action_description'] !== undefined && parsedMessage['action_description'] !== null && parsedMessage['action_description'] !== '') {
                parsedMessage['action_description'] = parsedMessage['action_description'].replace('Taking action Internet Search:', 'Taking action: Searching:');
                setDeepAgentStatus(parsedMessage['action_description']);
              }
            }
            else if(parsedData.type === 'deep_agent_reference') {
              upsertById(deep_agent_obj.references, parsedMessage);
            }
            else if (
              parsedData.type === 'deep_agent' &&
              parsedMessage['final_response'] !== undefined &&
              parsedMessage['final_response'] !== null &&
              parsedMessage['final_response'] !== '' &&
              parsedMessage['status'] === 'completed' &&
              typeof parsedMessage['final_response'] === 'string'
            ) {
              setDeepAgentResponse(parsedMessage['final_response']);
              if (setDeepAgentFinalResponse) {
                setDeepAgentFinalResponse(parsedMessage['final_response']);
              }
              if (addOrUpdatFinalMessage) {
                addOrUpdatFinalMessage(deep_agent_id, parsedMessage['final_response']);
              }
              upsertById(deep_agent_obj.actions, {
                "action_description": "Deep Agent completed.",
                "action_class": "CompletionResponseAction",
                "outputs": "{\"message\": \"Yes\", \"embedding_down\": false, \"vectors_down\": false, \"uuid\": \"999999\", \"references\": \"\", \"added_obj\": null, \"type\": \"completion\", \"tool_calls\": null, \"usage\": null}",
                "deep_agent_id": deep_agent_id,
                "id": 999999,
                "metadata_json": null
            });
              setDeepAgentStatus('Deep Agent request completed');
              setDeepAgentFinished(true);
            }else if (
              parsedData.type === 'deep_agent' &&
              parsedMessage['final_response'] !== undefined &&
              parsedMessage['final_response'] !== null &&
              parsedMessage['final_response'] !== '' &&
              (parsedMessage['status'] === 'errored' || parsedMessage['status'] === 'errored - retryable') &&
              typeof parsedMessage['final_response'] === 'string'
            ) {
              setDeepAgentResponse(parsedMessage['final_response']);
              if (setDeepAgentFinalResponse) {
                setDeepAgentFinalResponse(parsedMessage['final_response']);
              }
              if (addOrUpdatFinalMessage) {
                addOrUpdatFinalMessage(deep_agent_id, parsedMessage['final_response']);
              }
              upsertById(deep_agent_obj.actions, {
                "action_description": parsedMessage['final_response'],
                "action_class": "ErrorResponseAction",
                "outputs": "{\"message\": \"Yes\", \"embedding_down\": false, \"vectors_down\": false, \"uuid\": \"999999\", \"references\": \"\", \"added_obj\": null, \"type\": \"completion\", \"tool_calls\": null, \"usage\": null}",
                "deep_agent_id": deep_agent_id,
                "id": 999999,
                "metadata_json": null
            });
              setDeepAgentStatus('Deep Agent failed');
            }
            setDeepAgentInfos({
              infos: [...deep_agent_obj.infos],
              actions: [...deep_agent_obj.actions],
              references: [...deep_agent_obj.references]
            });
          }
        }
        catch (error) {
          log.error('Error parsing message:', error);
        }
      }
      return true;
    } catch (error) {
      if(log_error) {
        log.error('Error parsing JSON:', error, data);
      }
      return null;
    } 
  }

  const parsedReferences = useMemo(() => {
    if (deep_agent_infos && deep_agent_infos.references) {
      return deep_agent_infos.references.map((reference) => {

        let json_data = null;
        try {
          json_data = JSON.parse(reference.json_data);
        } catch (error) {
          log.error('parsedReferences: 2: Error parsing JSON:', error);
        }

        let metadata_data = null;
        try {
          metadata_data = JSON.parse(reference.metadata_json);
        } catch (error) {
          log.error('parsedReferences: 2: Error parsing JSON:', error);
        }

        let message = '';
        let description = '';
        if(reference.url !== undefined && reference.url !== null && reference.url.length > 0) {
          message= json_data['Article Title']
          description = json_data['Article Description']
        }
        else {
          const description_obj = metadata_data.combined;
          message = description_obj.length > 200 ? description_obj.slice(0, 200) + "..." : description_obj;
          description = description_obj.length > 500 ? description_obj.slice(0, 500) + "..." : description_obj;
        }
        return {
          ...reference,
          json_data: json_data,
          message: message,
          description: description,
        }
      });
    }
    return [];
  }, [deep_agent_infos]);

  useEffect(() => {
    // if deep_agent_id changes, fetch the deep agent info
    if (deep_agent_id) {
      fetchDeepAgentInfo();
    }
  }
  , []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <Card 
        sx={{
          backgroundColor: theme.palette.appBackground,
          borderRadius: '12px',
          '--Paper-overlay': 'transparent',
          padding: '8px',
          width: '100%',
          maxWidth: '600px',
          cursor: 'pointer', // Makes the cursor a pointer
        }}
        onClick={onClick}
      >
          <Typography variant="body2" sx={{
          color: theme.palette.text.disabled,
          fontSize: '12px',
        }} >
          Deep Agent (tap to see more)
        </Typography>
        <Typography variant="body1" sx={{
          color: theme.palette.text.primary,
          fontSize: '14px',
        }} >
          {sanitizeHtml(deep_agent_status)}
        </Typography>
      </Card> 
      {deep_agent_error_message && deep_agent_error_message !== '' &&
        <div style={{ marginTop: 8 }}>
          <Typography variant="body2" sx={{
            color: theme.palette.error.main,
            fontSize: '14px',
          }} >
            {deep_agent_error_message}
          </Typography>
        </div>
      }
      {deep_agent_response !== '' &&
        <div style={{ marginTop: 8 }}>
          <MarkdownRenderer
            content={deep_agent_response}
            user="deepagent"
            type="deepagent"
            setDeepAgentFinalResponse={()=>{}}
          />
        </div>      
      }
      {deep_agent_finished && deep_agent_infos.references && deep_agent_infos.references.length > 0 && (
        <Alert
          sx={{
            marginTop: 2
          }}
          severity="info"
        >
          <AlertTitle>References</AlertTitle>
          {parsedReferences.map((reference, index) => (
            <Stack direction={'row'} spacing={0} alignItems={'center'} key={`reference-${index}`}>
              <Reference
                message={reference.message}
                description={reference.description}
                explainability={showExplainability}
                url={reference.url ? reference.url : ''}
                key={`reference-${index}`}
                index={index + 1}
              />
              { reference.url && reference.url.length > 0 && reference.url !== '' && (
              <IconButton
                component="a"
                href={reference.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: theme.palette.text.primary,
                  height: '32px',
                }}
              >
                <MdOutlineOpenInNew style={{ width: '16px' }} alt="Link to Article" />
              </IconButton>)}
            </Stack>
          ))}
          <Button
            variant="contained"
            color="primary"
            size='small'
            onClick={() => setShowExplainability(!showExplainability)}
          >
            {showExplainability ? 'Hide Explainability' : 'Show Explainability'}
          </Button>
        </Alert>
      )}
    </div>
  );
};